import { useCallback, useMemo } from 'react'
import { Venue, VenueEvent } from 'types/model'

import { Button } from '@elements/button'
import { FormattedContent } from '@elements/formatted-content'
import { PosterImage } from '@elements/poster-image'
import { SeeMore } from '@elements/see-more'

import { useBreakpoint } from '@hooks/breakpoints'
import { breakpoints } from '@utils/breakpoints'
import { Breakpoint } from '@elements/breakpoint'
import { Skeleton } from '@elements/skeleton'
import classNames from 'classnames'
import { PlaceDisplay } from '@components/place-display'
import { TimeDisplay } from '@components/time-display'

export interface EventListItemProps {
  venue: Venue
  event: VenueEvent
  onClick: (event: VenueEvent) => void | Promise<void>
}

function _EventListItem({ venue, event, onClick }: EventListItemProps) {
  const styles = useEventListItemStyle({ featured: event.featured })
  const screenXxs = useBreakpoint(breakpoints.xxs)
  const screenXs = useBreakpoint(breakpoints.xs)
  const screenSm = useBreakpoint(breakpoints.sm)
  const screenMd = useBreakpoint(breakpoints.md)
  const screenLg = useBreakpoint(breakpoints.lg)

  const descriptionLineClamp = useMemo(() => {
    switch (true) {
      case screenLg:
      case screenMd:
      case screenSm:
        return 3
      default:
        return 2
    }
  }, [screenLg, screenMd, screenSm])

  const posterImageWidth = useMemo(() => {
    switch (true) {
      case screenLg:
      case screenMd:
        return '248'
      case screenSm:
        return '216'
      case screenXs:
        return '148'
      case screenXxs:
        return '124'
      default:
        return '104'
    }
  }, [screenXxs, screenXs, screenSm, screenMd, screenLg])

  const posterImageHeight = useMemo(() => {
    switch (true) {
      case screenLg:
      case screenMd:
        return '325'
      case screenSm:
        return '283'
      case screenXs:
        return '194'
      case screenXxs:
        return '162'
      default:
        return '136'
    }
  }, [screenXxs, screenXs, screenSm, screenMd, screenLg])

  const handleClick = useCallback(() => {
    onClick(event)
  }, [event, onClick])

  return (
    <div className={styles.container}>
      {event.featured && (
        <span className='absolute -top-2 -left-2 px-2 py-1 bg-primary-300 text-white z-10 border border-neutral-400 uppercase rounded-md drop-shadow-md text-sm font-bold shimmer'>
          Featured
        </span>
      )}
      {event.primaryImage && (
        <div className={styles.poster} onClick={handleClick}>
          <PosterImage
            src={event.primaryImage.filename}
            alt={event.name}
            w={posterImageWidth}
            h={posterImageHeight}
          />
        </div>
      )}
      <div className={styles.info}>
        <div className={styles.details}>
          <span className='heading-md sm:heading-lg md:heading-xl font-bold !text-left'>
            {event.name}
          </span>
          <Breakpoint.XXS>
            <SeeMore
              clamp={descriptionLineClamp}
              className='text-sm xs:text-base sm:text-md'
            >
              <FormattedContent className='!pb-0 sm:!pb-0 space-y-0.5'>
                {event.description
                  ?.split('\n')
                  .filter((line) => !!line)
                  .join('<br/>')}
              </FormattedContent>
            </SeeMore>
          </Breakpoint.XXS>
        </div>
        <div className='space-y-1 sm:space-y-2'>
          <div className='w-full flex items-end justify-between !text-left'>
            <div className='flex-shrink-0'>
              <TimeDisplay
                dayClassName='heading-base sm:heading-lg'
                timeClassName='text-xs sm:text-sm lg:text-base'
                dateTime={event.dateStart}
                timezone={event.timezone || venue.location.timezone}
              />
            </div>
            {venue.location.address && (
              <div className='max-w-1/2 ml-3 truncate'>
                <PlaceDisplay
                  className='text-sm sm:text-base ellipsis truncate'
                  place={venue.name}
                  address={venue.location.address}
                />
              </div>
            )}
          </div>
          <Button.Primary
            onClick={handleClick}
            className='w-full uppercase text-sm md:!text-base md:!p-2.5'
            sm
          >
            Book Now
          </Button.Primary>
        </div>
      </div>
    </div>
  )
}

function EventListItemSkeleton() {
  const styles = useEventListItemStyle()

  return (
    <div className={styles.container}>
      <Skeleton
        className={classNames(
          styles.poster,
          'aspect-[625/750] md:!aspect-[675/750] lg:!aspect-square !rounded-none'
        )}
      />
      <div className={styles.info}>
        <div className={styles.details}>
          <Skeleton className='h-5 w-24 sm:h-6 md:h-8' />
          <Skeleton className='h-[12px] w-full' />
        </div>
        <div className='space-y-2 sm:space-y-2'>
          <div className='flex items-end justify-between w-full'>
            <div className='space-y-1'>
              <Skeleton className='w-30' />
              <Skeleton className='w-20' />
            </div>
            <Skeleton />
          </div>
          <Skeleton className='h-[32px] sm:h-[44px] w-full rounded' />
        </div>
      </div>
    </div>
  )
}

function useEventListItemStyle({
  featured = false,
}: { featured?: boolean } = {}) {
  return useMemo(
    () => ({
      container: classNames('relative @container flex bg-card shadow-lg', {
        'border-2 border-primary-600': featured,
      }),
      poster:
        'flex flex-col justify-center w-[104px] xxs:w-[124px] xs:w-[148px] sm:w-[216px] md:w-[248px] cursor-pointer',
      info: 'flex-1 flex flex-col justify-between p-3 lg:p-4',
      details: 'space-y-1 xxs:space-y-1.5 pb-3 sm:pb-5',
    }),
    []
  )
}

export const EventListItem = Object.assign(_EventListItem, {
  Skeleton: EventListItemSkeleton,
})
